
import React, { useState, useEffect } from 'react';
import { Lock, ArrowRight, ShieldCheck, BarChart3, AlertTriangle, Scale, BrainCircuit } from 'lucide-react';
import { clsx } from 'clsx';

interface AuthGateProps {
  children: React.ReactNode;
}

export const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    // Check if previously authenticated
    const auth = localStorage.getItem('lean_controls_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (passcode === 'lean-2025') {
      localStorage.setItem('lean_controls_auth', 'true');
      setIsAuthenticated(true);
      setError(false);
    } else {
      setError(true);
      setPasscode('');
    }
  };

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="h-screen w-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-white rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-700">

        {/* Left: Value Props */}
        <div className="md:w-3/5 bg-zinc-50 p-12 flex flex-col justify-between relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

          <div>
            <div className="w-12 h-12 bg-black text-white rounded-xl flex items-center justify-center mb-8 shadow-xl">
              <span className="font-black text-xl tracking-tighter">GF</span>
            </div>
            <h2 className="text-3xl font-black tracking-tight mb-4">Complete Control.<br />Zero Complexity.</h2>
            <p className="text-zinc-500 font-medium leading-relaxed max-w-sm">
              The power of Primavera P6 without the PhD. Built for contractors who need to build, not just schedule.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 mt-12 z-10">
            <FeatureRow icon={<BarChart3 />} title="Interactive Gantt" desc="Visual planning with drag-and-drop simplicity." />
            <FeatureRow icon={<AlertTriangle className="text-amber-500" />} title="Conflict Alerts" desc="Prevents logic breaks without auto-moving your dates." />
            <FeatureRow icon={<Scale className="text-rose-500" />} title="Claims Defense" desc="Baseline snapshots and variance tracking built-in." />
            <FeatureRow icon={<BrainCircuit className="text-violet-500" />} title="AI Analysis" desc="Automated delay report generation." />
            <FeatureRow icon={<ShieldCheck className="text-emerald-500" />} title="Doc Intelligence" desc="RAG-powered analysis of your site PDFs." />
          </div>

          <div className="mt-12 pt-8 border-t border-zinc-200">
            <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">© 2026 Project Controls Advisor • Enterprise Edition</p>
          </div>
        </div>

        {/* Right: Login */}
        <div className="md:w-2/5 bg-white p-12 flex flex-col justify-center border-l border-zinc-100">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-zinc-100 text-black rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock size={24} />
            </div>
            <h1 className="text-xl font-black uppercase tracking-tight mb-2">Welcome Back</h1>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Enter Access Code</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <input
              type="password"
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value);
                setError(false);
              }}
              className={clsx(
                "w-full bg-zinc-50 text-center font-bold text-lg tracking-[0.5em] py-5 px-4 rounded-2xl outline-none border-2 transition-all placeholder:tracking-normal placeholder:text-sm placeholder:font-medium",
                error
                  ? "border-rose-100 text-rose-600 focus:border-rose-500 placeholder:text-rose-300 bg-rose-50/50"
                  : "border-transparent focus:border-black focus:bg-white text-black placeholder:text-zinc-300"
              )}
              placeholder="PASSCODE"
              autoFocus
            />

            <button
              type="submit"
              className="w-full bg-black text-white font-black uppercase tracking-widest text-xs py-5 rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-xl flex items-center justify-center gap-2 group"
            >
              <span>Enter System</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
          <div className="mt-8 text-center">
            <p className="text-[9px] font-bold text-zinc-300 uppercase">
              {error ? <span className="text-rose-500">Invalid Credentials</span> : "Secured Access"}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

const FeatureRow = ({ icon, title, desc }: { icon: React.ReactNode, title: string, desc: string }) => (
  <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-zinc-200">
    <div className="mt-1 text-zinc-900">{icon}</div>
    <div>
      <h4 className="font-bold text-sm text-zinc-900">{title}</h4>
      <p className="text-xs text-zinc-500 font-medium mt-1">{desc}</p>
    </div>
  </div>
);
